// ----------------------------------------------------------
// © Copyright 2021 European Space Agency, 2021
// This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this [source code/executable] package. No part of the package, including this file, may be copied, modified, propagated, or distributed except according to the terms contained in the file ‘LICENSE.txt’.
// -----------------------------------------------------------
import { createStore } from "@stencil/store";
import { Logger } from "../global/logger";
import { isDefined } from "./check";
const logger = new Logger("component", "utils");
const trim = (element) => isDefined(element.trim) ? element.trim() : element;
// @ts-ignore
if (!Array.prototype.trim) {
    // Remove the empty strings and the nullish object
    //@ts-expect-error
    Array.prototype.trim = function () {
        return this.map(element => isDefined(element) && trim(element)).filter(element => !!element);
    };
}
/**
 * Sort any object on any field from the most recent to the oldest. Must be used in a array sort function
 * @param key the key to compare the objects
 * @param type is the direction Ascendent or Descendent
 * @returns a method capable of comparing objects
 */
export const by = (key, type = "ASC") => (A, B) => {
    // return 1 or -1 depending on the type of comparison ASC won't change anything DESC or something else will invert
    return (type == "ASC" ? 1 : -1) * (A[key] > B[key] ? 1 : -1);
};
/**
 * Flatten an array of object to make it searcheable
 * it adds & before and after each element so that a string search can't be confused
 * by 2 string contactenated and creating a new one creating false positive
 * @param array
 * @param field
 * @returns
 */
export const flatten = (array, field = "id") => {
    return "&" + array.map(item => item[field]).join('&') + "&";
};
/**
 * Simple reducer for boolean array
 * @param acc
 * @param value
 * @returns
 */
export const bReducer = (acc, value) => acc && value;
/**
 * Transform a IMetadataGQ into a name/value pair
 * @param metadataDefinition
 */
export const stringValue = (metadataDefinition) => {
    return { name: metadataDefinition.name, value: metadataDefinition.value };
};
/**
 * Build an object based on an array of object which will have as a key the objectKey from the source object and body will
 * be a summary of the build and a list of build versions
 * @param list
 * @param objectKey
 * @returns
 */
export const mapBuildsToObject = (list, objectKey) => {
    return list.reduce((accumulator, item) => {
        const keyValue = item[objectKey];
        // check if it exists and if yes add the build to the list
        if (accumulator[keyValue]) {
            accumulator[keyValue].push(item);
        }
        else {
            accumulator[keyValue] = [item];
        }
        return accumulator;
    }, {});
};
export const pipe = (...fns) => (x) => fns.reduce((v, f) => f(v), x);
// No operation function, help to default a method not provided for instance
export const noop = () => { };
/**
 * Private recursive method for expending the JSON dotted notation
 * @param json data
 * @param parts keys from the dottted notation, this is the variable for the recursion
 */
const getExpendedContent = (json, parts) => {
    logger.info(json, parts);
    return parts.length == 0
        ? json
        : {
            [parts[0]]: getExpendedContent(json, parts.slice(1)),
        };
};
/** Create a deep reactive object, going in each branch to add the reactivity proxy using the stenciljs store which is not doing it
 * by default. Stenciljs store is adding reactivity only at root level of the object, this method will deep walk in the object to have this
 * at every level of the object
 */
export const createDeepStore = (defaultState) => {
    let newO = defaultState;
    // check that we have an object and we may have subobject
    if (typeof newO == 'object') {
        Object.keys(newO).forEach((key) => {
            if (typeof newO[key] == 'object') {
                // create a deep store on object
                newO[key] = createDeepStore(newO[key]);
            }
        });
    }
    return createStore(newO);
};
export function mult(...args) {
    let div = 1;
    return args.reduce(function (mult, num) {
        const s = num + '';
        const pos = s.indexOf('.');
        let n = num;
        if (pos > -1) {
            const p = s.substr(pos + 1).length;
            div *= Math.pow(10, p);
            n *= Math.pow(10, p);
        }
        return mult * n;
    }, 1) / div;
}
/**
 * Transform an Edisoft running datalab (dlp) into an SCIAPPS running instance
 *
 */
export function datalabToInstanceProperty(builds) {
    const buildsMap = mapBuildsToObject(builds, "id");
    return (datalab) => {
        // This code handles the creation and retrieve of the datalab running instances
        const resources = datalab.resources ? datalab.resources : [];
        const buildId = datalab.dlp ? datalab.dlp.name : datalab.dlpId;
        const description = datalab.dlp ? datalab.dlp.description : null;
        const name = datalab.dlp ? datalab.dlp.name : datalab.name;
        // hardcoded 0 is ok as the object is indexed by its id, if null it could be because the build is not shared/moderated
        const datalabId = buildsMap[buildId] ? buildsMap[buildId][0].datalabId : null;
        return { name: datalab.name, datalabName: name, description, datalab: datalabId, id: datalab.id, launchRef: datalab.launchRef, build: buildId, resources, status: datalab.state, categories: datalab.categories };
    };
}
